import { format, utcToZonedTime } from 'date-fns-tz'
import { Accordion } from 'shared/components/Accordion/Accordion'
import { FlexBox, Grid } from 'shared/components/Base/Base'
import { Text } from 'shared/components/Text/Text'
import { getDateTimeWindow } from 'shared/helpers/formatters'
import { PerformanceEvent, UserPerformanceEvent } from 'shared/models/performance'
import { darkGray, errorD1, informationL1, midGray, primaryBlack } from 'shared/theme/colors'
import styled from 'styled-components'

interface PerformanceEventAccordionProps {
  performanceEvent: PerformanceEvent
  isExpanded: boolean
}

// TODO: @public-performance-scores fix the timezone
const timeZone = 'America/Los_Angeles'
const formatString = 'MMM d, yyyy • HH:mm zzz'

const formatAppointmentTime = (event: UserPerformanceEvent) => {
  return event.appointmentStartTime && event.appointmentEndTime
    ? getDateTimeWindow(event.appointmentStartTime, event.appointmentEndTime, timeZone, {
        addYears: true,
        addBullet: true,
      })
    : event.appointmentStartTime
    ? format(event.appointmentStartTime, formatString, { timeZone })
    : event.appointmentEndTime
    ? format(event.appointmentEndTime, formatString, { timeZone })
    : 'Missing data'
}

const formatTimeLate = (mins: number) => {
  return mins < 60
    ? `${mins} min`
    : mins < 1440
    ? `${Math.floor(mins / 60)} hr ${mins % 60} min`
    : `${Math.floor(mins / 1440)} d ${Math.floor((mins % 1440) / 60)} hr ${mins % 60} min`
}

export function PerformanceEventAccordion({ performanceEvent, isExpanded }: PerformanceEventAccordionProps) {
  return (
    <Accordion
      isExpanded={isExpanded}
      details={
        <ExpandedContainer isColumn>
          <FlexBox isColumn gap="40px">
            {performanceEvent.events
              .sort((a, b) => (a.eventDate > b.eventDate ? 1 : -1))
              .map((event, index) => (
                <Grid columns="1fr 1fr 375px 1fr" gap="40px">
                  <Text variant="uppercaseLabel3" fontColor={primaryBlack}>
                    STOP {index + 1}
                  </Text>

                  <FlexBox isColumn gap="8px">
                    <Text variant="uppercaseLabel3" fontColor={primaryBlack}>
                      {event.stopTypeDetail}
                    </Text>
                    <Text variant="header4" fontColor={primaryBlack}>
                      {event.stopType === 'ORIGIN' ? event.originCityState : event.destinationCityState}
                    </Text>
                  </FlexBox>

                  <FlexBox isColumn gap="8px">
                    <Grid columns="1fr 250px" gap="20px">
                      <Text variant="header4" fontColor={darkGray}>
                        Appointment:
                      </Text>
                      <Text variant="header4" fontColor={primaryBlack}>
                        {formatAppointmentTime(event)}
                      </Text>
                    </Grid>
                    <Grid columns="1fr 250px" gap="20px">
                      <Text variant="header4" fontColor={midGray}>
                        Check in:
                      </Text>
                      <Text variant="header4" fontColor={midGray}>
                        {event.firstArrivalDate
                          ? format(utcToZonedTime(event.firstArrivalDate, timeZone), formatString, {
                              timeZone,
                            })
                          : 'Missing data'}
                      </Text>
                    </Grid>
                  </FlexBox>

                  <Text
                    variant="header4"
                    fontColor={
                      event.isOnTime || !(event.firstArrivalDate && event.appointmentEndTime)
                        ? darkGray
                        : errorD1
                    }
                  >
                    {event.isOnTime
                      ? 'On-time'
                      : event.timeFromArrivalToAppointmentEndMinutes
                      ? `${formatTimeLate(event.timeFromArrivalToAppointmentEndMinutes)} late`
                      : 'Missing data'}
                  </Text>
                </Grid>
              ))}
          </FlexBox>
        </ExpandedContainer>
      }
      hideExpandIcon
    >
      {() => <></>}
    </Accordion>
  )
}

const ExpandedContainer = styled(FlexBox)`
  padding: 24px 24px 24px 24px;
  background-color: ${informationL1};
`
